import moment from 'moment';

/**
 * ONE derivation for the mail list: base collection -> advanced filter -> search -> sort -> grouping.
 *
 * This replaces three competing useEffects in CommonMessage that each called setRenderMessage on
 * different dependency arrays. Whichever fired last won, so the controls did not compose: applying
 * the From/To/Subject filter and then clicking "Unread" silently dropped the filter, and any
 * reRender() — every star, archive, delete and the EmailCreated chain event — wiped an active
 * search while leaving the query text sitting in the search box.
 *
 * Everything here is pure so the caller can run it inside a useMemo and get a stable array
 * identity, which is also what stops Messages.js re-rendering forever.
 */

/** Keys, order and labels are kept identical to the mobile client's SORT_OPTIONS
 *  (Mobile/dmail-app-mobile/src/screens/Mainflow/BottomBar/inbox/index.tsx) so the two agree. */
export const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'unread', label: 'Unread first' },
  { value: 'sender', label: 'Sender (A–Z)' },
  { value: 'subject', label: 'Subject (A–Z)' },
];

export const DEFAULT_SORT = 'newest';

/** Milliseconds, or 0 when the row has no usable date (drafts/outbox). 0 for everything keeps the
 *  caller's original order, because Array.prototype.sort is stable. */
const messageTime = (message) => {
  const raw = message?.date || message?.created_at || message?.receivedDate;
  if (raw === undefined || raw === null || raw === '') return 0;
  // Web2 rows carry an epoch on some paths and an ISO string on others.
  if (typeof raw === 'number') return raw < 1e12 ? raw * 1000 : raw;
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const messageSender = (message) => {
  if (message?.groupKey) return String(message.groupKey).split('___')[0] || '';
  return String(message?.sender || '').toLowerCase();
};

const isUnread = (message) => {
  if (message?.groupKey && Array.isArray(message.data)) {
    return message.data.some((child) => !(child?.isRead || child?.read));
  }
  return !(message?.isRead || message?.read);
};

/** header is a JSON string on chain mail and sometimes already an object on web2 mail. A throw here
 *  used to take the whole list render down, so it is contained. */
const parseHeader = (message) => {
  const header = message?.header;
  if (!header) return {};
  if (typeof header === 'object') return header;
  try {
    return JSON.parse(header) || {};
  } catch (err) {
    return {};
  }
};

const asList = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value) return [value];
  return [];
};

export const flattenMessages = (list = []) =>
  (list || []).flatMap((item) =>
    item?.groupKey && Array.isArray(item.data) ? item.data : [item]
  ).filter(Boolean);

export const collectIds = (list = []) =>
  flattenMessages(list).map((item) => item?.id).filter((id) => id !== undefined && id !== null);

const messageSubject = (message) => {
  if (message?.groupKey) return String(message.groupKey).split('___')[1] || '';
  return String(message?.subject || '').toLowerCase();
};

export const sortMessages = (list = [], sortType = DEFAULT_SORT) => {
  const copy = [...list];
  switch (sortType) {
    case 'oldest':
      return copy.sort((a, b) => messageTime(a) - messageTime(b));
    case 'sender':
      return copy.sort((a, b) => messageSender(a).localeCompare(messageSender(b)));
    case 'subject':
      return copy.sort((a, b) => messageSubject(a).localeCompare(messageSubject(b)));
    case 'unread':
      return copy.sort((a, b) => {
        const diff = Number(isUnread(b)) - Number(isUnread(a));
        return diff !== 0 ? diff : messageTime(b) - messageTime(a);
      });
    case 'newest':
    default:
      return copy.sort((a, b) => messageTime(b) - messageTime(a));
  }
};

export const applySearch = (list = [], term = '') => {
  const needle = String(term || '').trim().toLowerCase();
  if (!needle) return list;
  return list.filter((data) => {
    const header = parseHeader(data);
    const recipients = asList(header.to).join(',').toLowerCase();
    return (
      String(data?.subject || '').toLowerCase().includes(needle) ||
      recipients.includes(needle) ||
      String(data?.sender || '').toLowerCase().includes(needle)
    );
  });
};

export const hasActiveFilter = (filters) =>
  !!(filters && (filters.from || filters.to || filters.subject || (filters.dateRange && filters.dateRange.length)));

export const applyAdvancedFilter = (list = [], filters) => {
  if (!hasActiveFilter(filters)) return list;

  const from = String(filters.from || '').trim().toLowerCase();
  const to = String(filters.to || '').trim().toLowerCase();
  const subject = String(filters.subject || '').trim().toLowerCase();
  const range =
    filters.dateRange && filters.dateRange.length === 2 && filters.dateRange[0] && filters.dateRange[1]
      ? filters.dateRange.map((date) => (date?.format ? date.format('YYYY-MM-DD') : moment(date).format('YYYY-MM-DD')))
      : null;

  return list.filter((data) => {
    const header = parseHeader(data);
    const sender = String(header.sender || data?.sender || '').toLowerCase();
    // Both sides lowercased. The needle used to be lowercased and the haystack left alone, so the
    // To filter missed every capitalised address.
    const recipients = [...asList(header.to), ...asList(header.cc), ...asList(header.bcc)]
      .join(',')
      .toLowerCase();
    const createdAt = moment(data?.created_at || data?.date || data?.receivedDate);

    const isFrom = from ? sender.includes(from) : true;
    const isTo = to ? recipients.includes(to) : true;
    const isSubject = subject ? String(data?.subject || '').toLowerCase().includes(subject) : true;
    const isInRange = range ? (createdAt.isValid() ? createdAt.isBetween(range[0], range[1], 'days', '[]') : false) : true;

    return isFrom && isTo && isSubject && isInRange;
  });
};

/** Collapse sender+subject runs of more than one message into a single expandable row. */
export const groupMessages = (list = [], sortType = DEFAULT_SORT) => {
  const grouped = list.reduce((acc, message) => {
    const key = `${message?.sender}___${message?.subject}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(message);
    return acc;
  }, {});

  const realGroups = Object.entries(grouped).filter(([, messages]) => messages.length > 1);
  if (realGroups.length === 0) return list;

  const groupedIds = new Set(realGroups.flatMap(([, messages]) => messages.map((m) => m.id)));
  const result = list.filter((message) => !groupedIds.has(message.id));
  realGroups.forEach(([key, value]) => {
    result.push({ groupKey: key, date: value[0].date, data: value, sender: value[0].sender, subject: value[0].subject });
  });
  // Re-apply the active sort so group rows land in the same order the user asked for, instead of
  // always being forced back to newest-first.
  return sortMessages(result, sortType);
};

/**
 * The whole pipeline. `base` is the already-chosen collection (all / read / unread).
 */
export const buildRenderList = ({ base = [], filters, searchTerm = '', sortType = DEFAULT_SORT, isGrouped = false }) => {
  let list = applyAdvancedFilter(base, filters);
  list = applySearch(list, searchTerm);
  list = sortMessages(list, sortType);
  return isGrouped ? groupMessages(list, sortType) : list;
};
